import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import utils from "../../universal/utils";
import { DIALOG_PAPER_RADIUS } from "../common/dialogActionButtonSx";

const basePath = utils.getEdgeBasePath();
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const dialogPaperSx = {
  background: "linear-gradient(145deg, #16181a 0%, #222528 55%, #1a1c1f 100%)",
  border: "1px solid rgba(232, 163, 61, 0.28)",
  borderRadius: DIALOG_PAPER_RADIUS,
  boxShadow: "0 24px 64px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255,255,255,0.04) inset",
  overflow: "hidden",
  maxWidth: "440px",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "rgba(255,255,255,0.03)",
    "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    "&:hover fieldset": { borderColor: "rgba(232, 163, 61, 0.45)" },
    "&.Mui-focused fieldset": { borderColor: "#E8A33D" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#E8A33D" },
};

function isValidEmail(value) {
  return EMAIL_RE.test(String(value || "").trim());
}

/**
 * Non-blocking, dismissible prompt shown once to PlaySafe sign-ins.
 *
 * Signing in with PlaySafe already implies acceptance of our Terms of
 * Service (see docs/edge/terms-acceptance.md), so this dialog no longer
 * gates anything — it only offers an optional recovery email for players
 * who don't have one on file yet. Users can skip it entirely.
 */
export default function PlaySafeOnboardingDialog({ open, onComplete, onDismiss }) {
  const [email, setEmail] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setEmail("");
    setSubmitting(false);
    setError(null);
  }, [open]);

  async function handleSave() {
    const trimmedEmail = String(email || "").trim();
    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      setError("Please enter a valid email address or leave the field empty.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(`${basePath}/api/v1/account/playsafe-onboarding`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...(trimmedEmail ? { email: trimmedEmail } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        throw new Error(data?.message || "Failed to save your preferences");
      }

      await onComplete?.();
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  }

  function handleSkip() {
    onDismiss?.();
  }

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xs"
      onClose={() => {
        handleSkip();
      }}
      PaperProps={{ sx: dialogPaperSx }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.72)",
            backdropFilter: "blur(6px)",
          },
        },
      }}
    >
      <Box sx={{ px: 3, pt: 3.5, pb: 3 }}>
        <Box sx={{ textAlign: "center", mb: 2.5 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: "auto",
              mb: 1.5,
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(135deg, rgba(232,163,61,0.18) 0%, rgba(232,163,61,0.06) 100%)",
              border: "1px solid rgba(232, 163, 61, 0.35)",
              boxShadow: "0 0 24px rgba(232, 163, 61, 0.12)",
            }}
          >
            <VerifiedUserOutlinedIcon sx={{ fontSize: 30, color: "#E8A33D" }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              letterSpacing: "-0.02em",
              background: "linear-gradient(90deg, #fff 0%, rgba(255,255,255,0.82) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Welcome to Ouro
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.55 }}>
            You&apos;re all set — signing in with PlaySafe already covers our terms.
            Want account updates? Add an email (optional).
          </Typography>
        </Box>

        <TextField
          label="Email address (optional)"
          type="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !submitting) {
              handleSave();
            }
          }}
          fullWidth
          size="small"
          sx={{ ...fieldSx, mb: error ? 1.5 : 2.5 }}
          inputProps={{ maxLength: 254 }}
          helperText="We'll use this for important account notifications."
        />

        {error ? (
          <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
            {error}
          </Typography>
        ) : null}

        <Button
          component="a"
          href={`${basePath}/auth/playsafe/link-steam`}
          fullWidth
          sx={{
            py: 1.1,
            mb: 2,
            borderRadius: "10px",
            fontWeight: 700,
            textTransform: "none",
            fontSize: "0.9rem",
            backgroundColor: "rgba(255,255,255,0.06)",
            color: "rgba(255,255,255,0.88)",
            border: "1px solid rgba(255,255,255,0.14)",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.10)",
              borderColor: "rgba(255,255,255,0.22)",
            },
          }}
        >
          Link your Steam account
        </Button>

        <Divider sx={{ mb: 2, borderColor: "rgba(255,255,255,0.08)" }} />

        <Box sx={{ display: "flex", gap: 1.25 }}>
          <Button
            variant="outlined"
            fullWidth
            disabled={submitting}
            onClick={handleSkip}
            sx={{
              py: 1.2,
              borderRadius: "10px",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "0.95rem",
              borderColor: "rgba(255,255,255,0.16)",
              color: "rgba(255,255,255,0.72)",
              "&:hover": { borderColor: "rgba(255,255,255,0.28)" },
            }}
          >
            Skip
          </Button>
          <Button
            variant="contained"
            fullWidth
            disabled={submitting}
            onClick={handleSave}
            sx={{
              py: 1.2,
              borderRadius: "10px",
              fontWeight: 700,
              textTransform: "none",
              fontSize: "0.95rem",
              background: "linear-gradient(135deg, #E8A33D 0%, #c8872a 100%)",
              color: "#141414",
              boxShadow: "0 8px 24px rgba(232, 163, 61, 0.25)",
              "&:hover": {
                background: "linear-gradient(135deg, #eeb04f 0%, #d89435 100%)",
              },
            }}
          >
            {submitting ? (
              <>
                <CircularProgress size={18} sx={{ color: "inherit", mr: 1 }} />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

PlaySafeOnboardingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onComplete: PropTypes.func,
  onDismiss: PropTypes.func,
};
